import {debounceTime, first, share, switchMap} from 'rxjs/operators';
// THIS IS A STATIC CLASS SERVICE
import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {Injectable} from '@angular/core';
import {CreditCard} from 'angular-cc-library';

export class AsyncValidatorWExt {
  public static debounce(
    asyncValidator: (control: AbstractControl) => Observable<any>,
    time: number = 500): (control: AbstractControl) => Observable<any> {

    // the subject is the function returning an observable.
    // For the ValidationService, it has to be a static function taking
    // an argument of an AbstractControl and returning an observable.
    // The cool thing is that this can allow a web api call to handle validation
    // through an http.get call with the value of the control.
    const subject: Subject<AbstractControl> = new Subject();

    // create the good and error response map based on the returned values of the function
    // also set the debounce time to be what is passed in. The debounce will kil the previous
    // running of the last function call from the .next call.
    const observer: Observable<any> = subject.pipe(debounceTime(time),
      switchMap(abstractControl => asyncValidator(abstractControl)),
      share(),);

    observer.subscribe();

    return (control: AbstractControl) => {
      // Every time this function is invoked by Angular the framework needs to be told
      subject.next(control);

      // Need to take only one for every function invocation, because the subscription must complete.
      // Otherwise Angular form state would be "PENDING"
      return observer.pipe(first());
    };
  }
}

@Injectable()
export class ValidationService {

  private static config: { [id: string]: string; } = {
    'required': 'Field is required and cannot be empty',
    'invalidCreditCard': 'Is invalid credit card number',
    'invalidEmailAddress': 'Invalid email address',
    'invalidPasswordBasic': 'Invalid password. Password must be at least 6 characters long, and contain a number.',
    'invalidPwd': 'Password requirements aren\'t met',
    'isUnder13': 'You must be 13 years or older to register.',
    'invalidZip': 'Please enter a valid zip code',
    'email': 'Please enter a valid email address',
    'textMatch': 'Your password entries don\'t match',
    'doesMatch': 'Your password entries don\'t match',
    'invalidPhone': 'Invalid US phone number entered',
    'invalidDeliveryPhone': 'Please Enter a Valid Phone Number',
    'invalidGiftCardNumber': 'Please enter valid gift card number',
    'invalidGiftCardPin': 'Please enter valid 4 digit PIN',
    'requiredCardName': 'Please enter the name on your credit card.',
    'invalidCardName': 'Please enter the valid name on your credit card.',
    'requiredCardZip': 'Please enter the zip code',
    'invalidCardZip': 'Please enter a Valid ZIP code',
    'requiredState': 'Please Select a State',
    'invalidFavName': 'Favorite name will allow only alphanumeric values with space',
    'invalidCustName': 'Name must contain letters only',
    'invalidCustNameForCart': 'Name must contain letters, period, hyphen, apostrophe only',
    'requiredAddress': 'Please enter your address.',
    'requiredDeliveryAddress': 'Please Enter a Valid Address',
    'requiredCity': 'Please enter your city.',
    'invalidCityName': 'Please enter a valid city name.',
    'emailMatch': 'Emails don\'t match',
    'visitFrequencyRequired': 'Please choose a Visit Frequency',
    'restaurantOnlyNumber': 'The field Restaurant Number must be a number.',
    'invalidOrderNumber': 'Invalid order number',
    'invalidCurrency': 'Invalid order total',
    'noLocationInZip': 'No Whataburger locations found in your zip code. Whataburger only sponsors charities in areas they serve.',
    'ccNumber': 'Credit card information is invalid.',
    'validateExpDate': 'Please enter a valid Expiration Date',
    'requiredCard': 'Please enter a valid Credit / Debit card number',
    'expDate': 'Please enter a valid Expiration Date',
    'requiredExpDate': 'Please enter the expiration date',
    'cvc': 'Please enter the security code',
    'requiredCvc': 'Please enter a valid Security Code',
    'invalidText': 'Entered text is invalid',
    'Mask error': null,
    'futureDateTime': `Please enter a valid date`,
  };


  static getValidatorErrorMessage(validatorName: string, validatorValue?: any) {
    return this.config[validatorName];
  }

  // This is useful if doing an on the fly compose custom validator in the component
  // that is building the form to use a custom validation string that can be displayed
  // with the validation-message component.
  // EXAMPLE:
  /* ngOnInit() {

          this.validationService.addValidationMessage('domainMissing', 'Email require the wbhq domain');
           this.employeeForm = this.formBuilder.group({
               employeeEmail: ['', Validators.compose([Validators.required, this.myCustomValidation])]
           });
           this.employeeForm.valueChanges.subscribe((value)=> {
               console.log(value)
           })
       }

       //Our custom composed validation function
       public myCustomValidation(control: AbstractControl): {[key: string]: any;}  {
          var val: string = control.value;
          return (val.indexOf('@wbhq.com') === -1) ? { 'domainMissing' : true} : null;
       }; */

  static addValidationMessage(name: string, message: string) {
    this.config[name] = message;
  }

  static creditCardValidator(control: AbstractControl) {
    // Visa, MasterCard, American Express, Diners Club, Discover, JCB
    if (control.value) {
      const regEx = new RegExp('/^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])' +
        '[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/');
      if (!regEx.test(control.value)) {
        return {invalidCreditCard: true};
      }
    }
    return null;
  }

  static asyncEmailValidator(control: AbstractControl): Observable<any> {
    return new Observable<any>(
      (observer) => {
        if (control.value) {
          // tslint:disable-next-line:max-line-length
          const regEx = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
          if (!regEx.test(control.value)) {
            observer.next({invalidEmailAddress: true});
            observer.complete();
          }
        }
        observer.next(null);
        observer.complete();
      }
    );
  }

  static emailValidator(control: AbstractControl) {
    // RFC 5322 (Swift) compliance. http://emailregex.com/ longer regex aren't fast enough to handle
    // a key being pressed down withou creating a really complicated debounce code setup.

    if (control.value) {
      // const regEx = new RegExp(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/);
      const regEx = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
      if (!regEx.test(control.value)) {
        return {invalidEmailAddress: true};
      }
    }
    return null;
  }

  static validZip5(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d{5}$/);
      if (!regEx.test(control.value)) {
        return {invalidZip: true};
      }
    }
    return null;
  }

  static phoneValidator(control: AbstractControl) {
    if (control.value) {
      // accepts 10 digits only
      const regEx = new RegExp(/^[0-9]{10}$/);
      const invalidZeros = '0000000000';
      if (!regEx.test(control.value) || control.value === invalidZeros) {
        return {invalidPhone: true};
      }
    }
    return null;
  }

  static phoneDeliveryValidator(control: AbstractControl) {
    if (control.value) {
      // accepts 10 digits only
      const regEx = /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]‌)\s*)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-‌9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/;
      if (!regEx.test(control.value)) {
        return {invalidDeliveryPhone: true};
      }
    }
    return null;
  }

  static passwordValidatorBasic(control: AbstractControl) {
    // {6,100}           - Assert password is between 6 and 100 characters
    // (?=.*[0-9])       - Assert a string has at least one number
    if (control.value) {
      const regEx = new RegExp(/^(?=.*[0-9])[a-zA-Z0-9!@#$%^&*]{6,100}$/);
      if (!regEx.test(control.value)) {
        return {invalidPasswordBasic: true};
      }
    }
    return null;
  }


  static textMatch(control: AbstractControl) {
    if (control.value) {
      const first: string = control.get('control1').value as string;
      const second: string = control.get('control2').value as string;

      if (first !== second) {
        control.get('control2').setErrors({doesMatch: true});
      } else {
        control.get('control2').setErrors(null);
      }
    }
    return null;
  }

  static emailMatch(control: AbstractControl) {
    if (control.value) {
      const firstVal: string = control.get('control1').value as string;
      const secondVal: string = control.get('control2').value as string;

      if (firstVal !== secondVal) {
        control.get('control2').setErrors({emailMatch: true});
      } else {
        control.get('control2').setErrors(null);
      }
    }
    return null;
  }

  static passwordMatch(control: AbstractControl) {
    if (control.value) {
      const first: string = control.get('password').value as string;
      const second: string = control.get('confPw').value as string;

      if (!first || !second) {
        return null;
      }

      if (first !== second) {
        control.get('confPw').setErrors({doesMatch: true});
      } else {
        control.get('confPw').setErrors(null);
      }
    }
    return null;
  }

  static ageCheck(control: AbstractControl) {
    if (control.value) {
      const now = new Date();
      const enteredDate = new Date(control.value);
      const birthPlus13 = new Date(enteredDate.getFullYear() + 13, enteredDate.getMonth(), enteredDate.getDay());

      if (birthPlus13 > now) {
        return {isUnder13: true};
      }
    }
    return null;
  }

  static passwordValidator(control: AbstractControl) {
    if (control.value) {
      const pwdEntered = control.value;

      const lenReg = new RegExp('^.{8,20}$');
      const whiteSpaceReg = new RegExp('^[^\\s]*$');

      const numberReg = new RegExp('^.*(?=.*\\d)');
      const lowerCaseReg = new RegExp('^.*(?=.*[a-z])');
      const upperCaseReg = new RegExp('^.*(?=.*[A-Z])');
      const specialCharReg = new RegExp('^.*(?=.*[!*@#$%^&+=?/\\-_|:;])');

      const isValidLength = lenReg.test(pwdEntered);
      const isNoWhiteSpace = whiteSpaceReg.test(pwdEntered);
      const matchedCriterias = (numberReg.test(pwdEntered) ? 1 : 0)
        + (lowerCaseReg.test(pwdEntered) ? 1 : 0)
        + (upperCaseReg.test(pwdEntered) ? 1 : 0)
        + (specialCharReg.test(pwdEntered) ? 1 : 0);


      if (!isValidLength || !isNoWhiteSpace || matchedCriterias < 2) {
        return {invalidPwd: true};
      }
    }
    return null;
  }

  static validGiftCardNumber19(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d{19}$/);
      if (!regEx.test(control.value)) {
        return {invalidGiftCardNumber: true};
      }
    }
    return null;
  }

  static validGiftCardPin4(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d{4}$/);
      if (!regEx.test(control.value)) {
        return {invalidGiftCardPin: true};
      }
    }
    return null;
  }

  /*
  * Add Card - validation
  */

  static validateCardName(control: AbstractControl) {
    if (!control.value) {
      return {requiredCardName: true};
    } else if (control.value) {
      const regEx = new RegExp(/^[A-Za-z\s]+$/);
      if (!regEx.test(control.value)) {
        return {invalidCardName: true};
      }
    }
    return null;
  }

  static validateAddress(control: AbstractControl) {
    if (!control.value) {
      return {requiredAddress: true};
    } else if (!(control.value || '').trim().length) {
      return {requiredAddress: true};
    }
    return null;
  }

  static validateDeleteText(control: AbstractControl) {
    if (!control.value || !(control.value || '').trim().length) {
      return {required: true};
    } else if (control.value.toLowerCase() !== 'delete') {
      return {invalidText: true};
    }
    return null;
  }

  static validateState(control: AbstractControl) {
    if (!control.value) {
      return {requiredState: true};
    }
    return null;
  }

  static validateDeliveryAddress(control: AbstractControl) {
    if (!control.value) {
      return {requiredDeliveryAddress: true};
    } else if (!(control.value || '').trim().length) {
      return {requiredAddress: true};
    }
    return null;
  }

  static validateCity(control: AbstractControl) {
    if (!control.value) {
      return {requiredCity: true};
    } else if (control.value) {
      const regEx = new RegExp(/^[A-Za-z\s]+$/);
      if (!regEx.test(control.value) || !(control.value || '').trim().length) {
        return {invalidCityName: true};
      }
    }
    return null;
  }

  static validateCardZip(control: AbstractControl) {
    if (!control.value) {
      return {requiredCardZip: true};
    } else if (control.value) {
      const zip = control.value.toString();
      const regEx = new RegExp(/^\d{5}$/);
      if (!regEx.test(control.value) || zip === '00000') {
        return {invalidCardZip: true};
      }
    }
    return null;
  }

  static validateFavName(control: AbstractControl) {
    if (!control.value) {
      return {invalidFavName: true};
    } else {
      const controlVal = control.value.trim();
      const regEx = new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ\d\s'.-]+$/);
      if (!regEx.test(controlVal)) {
        return {invalidFavName: true};
      }
    }
    return null;
  }

  // Who is this for text validation
  static custNameValidatorForCart(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^[a-zA-Z '.-]+$/);
      if (!regEx.test(control.value)) {
        return {invalidCustNameForCart: true};
      }
    }
    return null;
  }

  // First and last name validation to be used across the app
  // Allows alpha, accented chars, spaces, periods, and hyphens
  static firstNameLastNameValidator(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ '.-]+$/);
      const isWhitespace = (control.value || '').trim().length === 0;
      const isValid = !isWhitespace;
      if (!regEx.test(control.value) || !isValid) {
        return {invalidCustName: true};
      }
    }
    return null;
  }

  static validateVisitFrequency(control: AbstractControl) {
    if (!control.value) {
      return {visitFrequencyRequired: true};
    }
    return null;
  }

  static validateRestaurantOnlyNumber(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d+$/);
      if (!regEx.test(control.value)) {
        return {restaurantOnlyNumber: true};
      }
    }
    return null;
  }

  static validateEmptiness(control: AbstractControl) {
    if (control.value) {
      const isEmpty = (control.value || '').trim().length === 0;
      if (isEmpty) {
        return {required: true};
      }
    }
    return null;
  }

  static validateCurrency(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^[0-9]{1,4}(\.[0-9]{0,2})?$/gm);
      if (!regEx.test(control.value)) {
        return {invalidCurrency: true};
      }
    }
    return null;
  }

  static validateDigitsOnly(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d+$/);
      if (!regEx.test(control.value)) {
        return {invalidOrderNumber: true};
      }
    }
    return null;
  }

  static validateOrderNumber(regex: RegExp): ValidatorFn {
    let forbidden = false;
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (regex) {
        forbidden = !regex.test(control.value);
      }
      return forbidden ? {invalidOrderNumber: true} : null;
    };
  }

  // necessary because orderNumber field on add visit was given
  // an abitrary 15 char limit, but input type="number" doesn't
  // work with maxlength; and pattern attribute wasn't showing
  // error message
  static validateOrderNumberLength(control: AbstractControl) {
    if (control.value) {
      const regEx = new RegExp(/^\d{1,15}$/);
      if (!regEx.test(control.value)) {
        return {invalidOrderNumber: true};
      }
    }
    return null;
  }

  static validateCCNumber(control: AbstractControl): ValidationErrors | null {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
      return {requiredCard: true};
    }

    const num = control.value.toString().replace(/\s+|-/g, '');

    if (!/^\d+$/.test(num)) {
      return {ccNumber: true};
    }

    const card = CreditCard.cardFromNumber(num);

    if (!card) {
      return {ccNumber: true};
    }

    if (card.length.includes(num.length) && (card.luhn === false || CreditCard.luhnCheck(num))) {
      return null;
    }

    const upperlength = card.length[card.length.length - 1];
    if (num.length > upperlength) {
      const registeredNum = num.substring(0, upperlength);
      if (CreditCard.luhnCheck(registeredNum)) {
        return null;
      }
    }

    return {ccNumber: true};
  }

  static validateExpDate(control: AbstractControl): ValidationErrors | null {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
      return {requiredExpDate: true};
    }

    if (typeof control.value !== 'undefined' && control.value.length >= 5) {
      let [month, year] = control.value.split(/[\s\/]+/, 2);

      if ((year != null ? year.length : void 0) === 2 && /^\d+$/.test(year)) {
        const prefix = new Date().getFullYear().toString().slice(0, 2);
        year = prefix + year;
      }

      month = parseInt(month, 10).toString();
      year = parseInt(year, 10).toString();

      if (/^\d+$/.test(month) && /^\d+$/.test(year) && (month >= 1 && month <= 12)) {
        const expiry = new Date(year, month);
        const currentTime = new Date();
        expiry.setMonth(expiry.getMonth() - 1);
        expiry.setMonth(expiry.getMonth() + 1, 1);

        if (expiry > currentTime) {
          return null;
        }
      }
    }

    return {expDate: true};
  }

  static validateCvc(control: AbstractControl): ValidationErrors | null {
    if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
      return {requiredCvc: true};
    }

    if (control.value.length >= 3 && control.value.length <= 4) {
      return null;
    }

    return {cvc: true};
  }
}
